/**
 * Safe Markdown presentation for governed Cognitory answers.
 * Formats only — never rewrites answer meaning. Raw HTML is sanitized away.
 */

import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";
import type { CSSProperties, ReactNode } from "react";
import {
  MAIN_PAGE_COLORS,
  MAIN_PAGE_FONT_STACK,
  MAIN_PAGE_RADIUS,
} from "../theme/mainPageVisual";

/** Sanitizer schema: allow GFM tables + common prose; strip raw HTML/scripts. */
export const COGNITORY_ANSWER_SANITIZE_SCHEMA = {
  ...defaultSchema,
  tagNames: [
    ...(defaultSchema.tagNames ?? []),
    "table",
    "thead",
    "tbody",
    "tr",
    "th",
    "td",
  ],
  attributes: {
    ...defaultSchema.attributes,
    table: [...(defaultSchema.attributes?.table ?? []), "className"],
    th: [...(defaultSchema.attributes?.th ?? []), "align"],
    td: [...(defaultSchema.attributes?.td ?? []), "align"],
    code: [...(defaultSchema.attributes?.code ?? []), "className"],
    a: ["href", "title"],
  },
  protocols: {
    ...defaultSchema.protocols,
    href: ["http", "https", "mailto"],
  },
} as const;

const tableWrapStyle: CSSProperties = {
  width: "100%",
  overflowX: "auto",
  margin: "0.75rem 0",
  borderRadius: MAIN_PAGE_RADIUS.input,
  border: `1px solid ${MAIN_PAGE_COLORS.panelBorder}`,
  background: "rgba(255, 255, 255, 0.55)",
};

const tableStyle: CSSProperties = {
  width: "100%",
  borderCollapse: "collapse",
  fontFamily: MAIN_PAGE_FONT_STACK,
  fontSize: "0.92rem",
  lineHeight: 1.45,
  color: MAIN_PAGE_COLORS.inkStrong,
};

const cellStyle: CSSProperties = {
  borderBottom: `1px solid ${MAIN_PAGE_COLORS.panelBorder}`,
  padding: "0.45rem 0.55rem",
  textAlign: "left",
  verticalAlign: "top",
};

const CITATION_MARKER_PATTERN = /\[\^(\d+)\]/g;

function renderCitationAwareText(
  text: string,
  onCitationMarkerClick?: (citationLabel: string) => void,
): ReactNode {
  if (!onCitationMarkerClick || !CITATION_MARKER_PATTERN.test(text)) {
    return text;
  }

  const parts: ReactNode[] = [];
  let lastIndex = 0;
  CITATION_MARKER_PATTERN.lastIndex = 0;
  let match: RegExpExecArray | null = CITATION_MARKER_PATTERN.exec(text);
  while (match) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    const citationLabel = match[0];
    parts.push(
      <button
        key={`${citationLabel}-${match.index}`}
        type="button"
        data-testid="cognitory-citation-marker"
        data-citation-label={citationLabel}
        onClick={() => onCitationMarkerClick(citationLabel)}
        style={{
          border: "none",
          background: "transparent",
          padding: 0,
          margin: 0,
          color: MAIN_PAGE_COLORS.link,
          cursor: "pointer",
          font: "inherit",
          verticalAlign: "super",
          fontSize: "0.75em",
        }}
      >
        {citationLabel}
      </button>,
    );
    lastIndex = match.index + citationLabel.length;
    match = CITATION_MARKER_PATTERN.exec(text);
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return parts;
}

function createMarkdownComponents(
  onCitationMarkerClick?: (citationLabel: string) => void,
): Components {
  const withCitationText = (children: ReactNode): ReactNode => {
    if (typeof children === "string") {
      return renderCitationAwareText(children, onCitationMarkerClick);
    }
    if (Array.isArray(children)) {
      return children.map((child, index) =>
        typeof child === "string" ? (
          <span key={`citation-text-${index}`}>
            {renderCitationAwareText(child, onCitationMarkerClick)}
          </span>
        ) : (
          child
        ),
      );
    }
    return children;
  };

  return {
    table: ({ children }) => (
      <div data-testid="cognitory-answer-table-wrap" style={tableWrapStyle}>
        <table data-testid="cognitory-answer-table" style={tableStyle}>
          {children}
        </table>
      </div>
    ),
    thead: ({ children }) => <thead>{children}</thead>,
    tbody: ({ children }) => <tbody>{children}</tbody>,
    tr: ({ children }) => <tr>{children}</tr>,
    th: ({ children }) => (
      <th
        style={{
          ...cellStyle,
          fontWeight: 500,
          color: MAIN_PAGE_COLORS.label,
          background: "rgba(248, 250, 252, 0.9)",
        }}
      >
        {children}
      </th>
    ),
    td: ({ children }) => (
      <td style={cellStyle}>{withCitationText(children)}</td>
    ),
    h1: ({ children }) => (
      <h1
        style={{
          fontSize: "1.15rem",
          fontWeight: 500,
          margin: "0.85rem 0 0.4rem",
          color: MAIN_PAGE_COLORS.inkStrong,
          fontFamily: MAIN_PAGE_FONT_STACK,
        }}
      >
        {children}
      </h1>
    ),
    h2: ({ children }) => (
      <h2
        style={{
          fontSize: "1.05rem",
          fontWeight: 500,
          margin: "0.75rem 0 0.35rem",
          color: MAIN_PAGE_COLORS.inkStrong,
          fontFamily: MAIN_PAGE_FONT_STACK,
        }}
      >
        {children}
      </h2>
    ),
    h3: ({ children }) => (
      <h3
        style={{
          fontSize: "1rem",
          fontWeight: 500,
          margin: "0.65rem 0 0.3rem",
          color: MAIN_PAGE_COLORS.inkStrong,
          fontFamily: MAIN_PAGE_FONT_STACK,
        }}
      >
        {children}
      </h3>
    ),
    p: ({ children }) => (
      <p
        style={{
          margin: "0 0 0.65rem",
          color: MAIN_PAGE_COLORS.inkStrong,
          fontFamily: MAIN_PAGE_FONT_STACK,
          fontSize: "1rem",
          lineHeight: 1.65,
          fontWeight: 300,
        }}
      >
        {withCitationText(children)}
      </p>
    ),
    ul: ({ children }) => (
      <ul
        style={{
          margin: "0 0 0.65rem",
          paddingLeft: "1.15rem",
          color: MAIN_PAGE_COLORS.inkStrong,
          fontFamily: MAIN_PAGE_FONT_STACK,
          lineHeight: 1.6,
        }}
      >
        {children}
      </ul>
    ),
    ol: ({ children }) => (
      <ol
        style={{
          margin: "0 0 0.65rem",
          paddingLeft: "1.15rem",
          color: MAIN_PAGE_COLORS.inkStrong,
          fontFamily: MAIN_PAGE_FONT_STACK,
          lineHeight: 1.6,
        }}
      >
        {children}
      </ol>
    ),
    li: ({ children }) => (
      <li style={{ marginBottom: 2 }}>{withCitationText(children)}</li>
    ),
    a: ({ href, children }) => (
      <a
        href={href}
        style={{ color: MAIN_PAGE_COLORS.link }}
        rel="noreferrer noopener"
      >
        {children}
      </a>
    ),
    // Keep citation markers like [^1] readable; optional click handler opens source card.
    sup: ({ children }) => (
      <sup style={{ fontSize: "0.75em", color: MAIN_PAGE_COLORS.soft }}>
        {withCitationText(children)}
      </sup>
    ),
  };
}

export type CognitoryAnswerMarkdownProps = {
  /** Exact governed answer / refusal text — must not be rewritten. */
  text: string;
  className?: string;
  onCitationMarkerClick?: (citationLabel: string) => void;
};

/**
 * Renders Markdown for presentation only. Input string is passed through
 * unchanged to the parser (no pre-processing / “improvement”).
 */
export default function CognitoryAnswerMarkdown(
  props: CognitoryAnswerMarkdownProps,
) {
  return (
    <div
      data-testid="cognitory-answer-text"
      data-cognitory-answer-markdown="safe"
      className={props.className}
      style={{
        color: MAIN_PAGE_COLORS.inkStrong,
        fontFamily: MAIN_PAGE_FONT_STACK,
        fontSize: "1rem",
        lineHeight: 1.65,
        fontWeight: 300,
      }}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[[rehypeSanitize, COGNITORY_ANSWER_SANITIZE_SCHEMA]]}
        components={createMarkdownComponents(props.onCitationMarkerClick)}
        // Disallow raw HTML passthrough at the markdown layer as defense in depth.
        skipHtml
      >
        {props.text}
      </ReactMarkdown>
    </div>
  );
}
